import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import menus from "./components/MenusLaterais";
import {
    IconButton,
    Theme,
    Toolbar,
    useTheme,
    Typography,
    Box,
    CssBaseline,
    useMediaQuery
} from "@mui/material";

import AppBar from '@mui/material/AppBar';
import MenuIcon from '@mui/icons-material/Menu';
import NotificacoesMenu from './components/Notificacoes/NotificacoesMenu';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { styled } from "@mui/material/styles";
import { apiServer } from "./api";
import { IEspacoUsado } from "./types/dashboard";
import { useLogin } from "./contexts/LoginContext";
import { useColorMode } from './App';
import MenuUsuario from './components/MenuUsuario'
import BFDrawer from "./components/MenusLaterais/Drawer";

interface Props {
    window?: () => Window;
}

const drawerWidth = 240;

const BFAppBar = styled(AppBar)(({ theme }) => ({
    position: "fixed",
    boxShadow: "none",
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: theme.palette.background.paper,
}));

const BFGrow = styled('div')({
    flexGrow: 1
});

const BFNotificationButton = styled(IconButton)(({ theme }) => ({
    paddingRight: theme.spacing(4)
}));

function Home(props: Props) {
    const theme: Theme = useTheme();
    const colorMode = useColorMode();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    
    const [menuAberto, setMenuAberto] = useState(isDesktop);
    const menuSelecionadoState = useState<JSX.Element | null>(null);
    const [menuSelecionado, setMenuSelecionado] = menuSelecionadoState;
    const [login] = useLogin();

    const tituloAtivoState = useState<string>('Dashboard');

    const espacoUsadoState = useState<IEspacoUsado | null>(null);
    const [, setEspacoUsado] = espacoUsadoState;

    useEffect(() => {
        setMenuAberto(isDesktop);
    }, [isDesktop]);

    const handleDrawerToggle = () => {
        setMenuAberto(!menuAberto);
    };

    const navigate = useNavigate();
    const location = useLocation();
    const [, setTituloAtivo] = tituloAtivoState;

    useEffect(() => {
        let currentPath = location.pathname;
        if (currentPath === '/') {
            navigate('/dashboard', { replace: true });
            return;
        }

        const matchedMenu = menus.find(m => m.rota === currentPath);
        if (matchedMenu) {
            setMenuSelecionado(matchedMenu.componente);
            setTituloAtivo(matchedMenu.titulo);
        }
    }, [location.pathname, navigate, setMenuSelecionado, setTituloAtivo]);

    const carregarTotalEspacoUsado = React.useCallback(() => {
        const cID = login.usuario?.usuarioCliente?.clienteID;
        if (!cID || !login.accessToken) return;

        apiServer<IEspacoUsado>()
            .get(`dashboard/espaco-usado/${cID}`, login.accessToken)
            .then((response) => {
                const espacoUsado = response.data;
                setEspacoUsado(espacoUsado);
            })
            .catch(() => {});
    }, [login.usuario?.usuarioCliente?.clienteID, login.accessToken, setEspacoUsado]);

    useEffect(() => {
        carregarTotalEspacoUsado();
    }, [carregarTotalEspacoUsado]);

    const usuarioCliente = login.usuario?.usuarioCliente;
    let nomeEmpresa = '';
    if (usuarioCliente) {
        const clienteObj = (usuarioCliente as any).Cliente || (usuarioCliente as any).cliente;
        if (clienteObj && clienteObj.pessoa && clienteObj.pessoa.nome) {
            nomeEmpresa = clienteObj.pessoa.nome;
        }
    }
    const identificadorEmpresa = nomeEmpresa 
        ? `${nomeEmpresa} (ID: ${usuarioCliente?.clienteID || ''})` 
        : `Empresa ID: ${usuarioCliente?.clienteID || 'Não identificado'}`;
    const usuarioNome = login.usuario?.nome || 'Não identificado';
    const usuarioEmail = login.usuario?.email || '';
    const dadosIdentificacao = `\n\n(Identificação - Empresa: ${identificadorEmpresa} | Usuário: ${usuarioNome} | E-mail: ${usuarioEmail})`;

    const acoesSuporte = [
        { 
            icon: <SupportAgentIcon />, 
            name: 'Suporte Técnico', 
            link: `https://wa.me/5562991240410?text=${encodeURIComponent(`Olá! Preciso de suporte técnico sobre o Backup Fácil.${dadosIdentificacao}`)}` 
        },
        { 
            icon: <MonetizationOnIcon />, 
            name: 'Financeiro', 
            link: `https://wa.me/5562991240410?text=${encodeURIComponent(`Olá! Gostaria de falar com o financeiro sobre o Backup Fácil.${dadosIdentificacao}`)}` 
        },
    ];

    return (<Box>
        <CssBaseline />

        <BFAppBar position="fixed" color="inherit">
            <Toolbar variant="dense"
                sx={{ 
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    minHeight: '56px'
                }}>
                <BFNotificationButton
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    onClick={handleDrawerToggle}>
                    <MenuIcon sx={{ color: theme.palette.text.primary }} />
                </BFNotificationButton>

                <BFGrow />

                <Typography
                    variant="h6"
                    onClick={() => navigate('/dashboard')}
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        caretColor: "transparent",
                        cursor: "pointer"
                    }}
                >
                    Backup Fácil
                </Typography>

                <BFGrow />

                <IconButton onClick={colorMode.toggleColorMode} color="inherit" sx={{ mr: 1 }}>
                    {theme.palette.mode === 'dark' ? (
                        <LightModeIcon sx={{ color: theme.palette.text.secondary }} />
                    ) : (
                        <DarkModeIcon sx={{ color: theme.palette.text.secondary }} />
                    )}
                </IconButton>

                <NotificacoesMenu />

                <MenuUsuario />
            </Toolbar>
        </BFAppBar>


        <BFDrawer 
            menuSelecionadoState={menuSelecionadoState}
            espacoUsadoState={espacoUsadoState}
            menuAbertoState={[menuAberto, setMenuAberto]} 
            tituloAtivoState={tituloAtivoState}
        />

        <Box
            component="main"
            sx={
                {
                    flexGrow: 1,
                    p: 3,
                    marginLeft: (menuAberto && isDesktop) ? `${drawerWidth}px` : '0',
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }
            }
        >
            <Box sx={{ paddingTop: "60px", minHeight: `calc(100vh - 50px)` }}>
                {menuSelecionado}
            </Box>
        </Box>

        {/* Botão de Suporte WhatsApp (SpeedDial Premium) */}
        <SpeedDial
            ariaLabel="Suporte WhatsApp"
            sx={{ position: 'fixed', bottom: 24, right: 24 }}
            icon={<SpeedDialIcon icon={<WhatsAppIcon />} />}
            FabProps={{ 
                sx: { 
                    bgcolor: '#25d366', 
                    color: '#fff',
                    '&:hover': { bgcolor: '#128c7e' } 
                } 
            }}
        >
            {acoesSuporte.map((acao) => (
                <SpeedDialAction
                    key={acao.name}
                    icon={acao.icon}
                    tooltipTitle={acao.name}
                    tooltipOpen
                    onClick={() => window.open(acao.link, '_blank')}
                    sx={{
                        whiteSpace: 'nowrap',
                        '& .MuiSpeedDialAction-staticTooltipLabel': {
                            fontWeight: 'bold',
                            color: theme.palette.text.primary,
                        }
                    }}
                />
            ))}
        </SpeedDial>
    </Box >);
}

export default Home;